import { React, PropTypes, APIPropTypes } from 'imports';
import { FormattedResource, Popup, TextInput, Tooltip, User, UserSelect } from '..';
import MarkdownBlock from '../MarkdownBlock/MarkdownBlock';
import Enum from 'utils/Enum';
import './PostForm.less';

export const AttachmentType = Enum('TEXT', 'IMAGE', 'USER');

export const buttonPropType = PropTypes.shape({
    visible: PropTypes.bool,
    content: FormattedResource.resourcePropType,
    tooltip: FormattedResource.resourcePropType
});

const imagePropType = PropTypes.shape({
    type: PropTypes.oneOf([AttachmentType.IMAGE]).isRequired,
    data: PropTypes.string.isRequired
});
const userPropType = PropTypes.shape({
    type: PropTypes.oneOf([AttachmentType.USER]).isRequired,
    data: PropTypes.number.isRequired
});
export const attachmentPropType = PropTypes.oneOfType([imagePropType, userPropType]);

const defaultProps = {
    submitButton: {
        visible: true,
        content: { id: 'common.post' },
        tooltip: null
    },
    cancelButton: {
        visible: false,
        content: { id: 'common.cancel' },
        tooltip: null
    },
    debounce: 300,
    disabled: false,
    multiline: true
};

export default class PostForm extends React.Component {
    static get AttachmentType() {
        return AttachmentType;
    }

    static get buttonPropType() {
        return buttonPropType;
    }

    static get attachmentPropType() {
        return attachmentPropType;
    }

    static get propTypes() {
        return {
            value: PropTypes.string,
            title: FormattedResource.resourcePropType,
            placeholder: FormattedResource.resourcePropType,
            formattable: PropTypes.bool,
            multiline: PropTypes.bool,
            attachmentTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AttachmentType))),
            attachments: PropTypes.arrayOf(attachmentPropType),
            valid: PropTypes.bool,
            disabled: PropTypes.bool,
            className: PropTypes.string,
            debounce: PropTypes.number,
            autofocus: PropTypes.bool,
            submitButton: buttonPropType,
            cancelButton: buttonPropType,
            onChange: PropTypes.func,
            onAttach: PropTypes.func,
            onDetach: PropTypes.func,
            onSubmit: PropTypes.func,
            onCancel: PropTypes.func
        };
    }

    static get defaultProps() {
        return defaultProps;
    }

    _fileReader = null;

    _timeoutID = null;

    constructor(props) {
        super(props);
        this._fileReader = new FileReader();
        this._fileReader.onload = this._onAttachImageLoaded;
        this.state = {
            value: props.value,
            userList: {
                visible: false,
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.value !== this.state.value) {
            if (this._timeoutID) {
                this._onChangeDebounced(this.state.value);
            } else {
                this.setState({ value: nextProps.value });
            }
        }
    }

    componentWillUnmount() {
        if (this._timeoutID) {
            this._onChangeDebounced(this.state.value);
        }
    }

    _onChangeDebounced = (value) => {
        if (this.props.onChange && value !== this.props.value) {
            this.props.onChange(this, value);
        }
        if (this._timeoutID) {
            clearTimeout(this._timeoutID)
        }
        this._timeoutID = null;
    }

    _onChange = (sender, value) => {
        if (this._timeoutID) {
            clearTimeout(this._timeoutID)
        }

        const isDebouncible = this.props.debounce && !(
            this.state.value && !value ||
            !this.state.value && value
        );

        if (isDebouncible) {
            this._timeoutID = setTimeout(this._onChangeDebounced.bind(this, value), this.props.debounce);
        }
        else {
            this._onChangeDebounced(value);
        }

        this.setState({ value });
    }

    _onSubmit = (sender, value, event) => {
        this.setState({ value });

        this._onChangeDebounced(value);

        if (this.props.onSubmit && (typeof this.props.valid === 'boolean' ? this.props.valid : (value || this.props.attachments && this.props.attachments.length))) {
            this.props.onSubmit(this, value, event);
        }
    }

    _onSubmitClick = (event) => {
        this._onSubmit(this, this.state.value, event);
    }

    _onCancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel(this);
        }
    }

    _onAttachImageClick = () => {
        this.refs.image.click()
    }

    _onAttachImageChange = () => {
        if (this.refs.image.files[0]) {
            this._fileReader.readAsDataURL(this.refs.image.files[0]);
        }
        this.refs.image.value = '';
    }

    _onAttachImageLoaded = (event) => {
        if (this.props.onAttach) {
            this.props.onAttach(this, { type: AttachmentType.IMAGE, data: this._fileReader.result });
        }
    }

    _onAttachUserClick = (event) => {
        if (!this.state.userListVisible) {
            const bounds = this.refs['attach-user'].getBoundingClientRect();
            this.setState({
                userList:
                bounds.top < window.innerHeight / 2 ?//TODO pretty stupid. can we track actual content size and move popup based on that?
                    {
                        visible: true,
                        left: bounds.left,
                        top: bounds.bottom + (document.scrollingElement || document.documentElement).scrollTop + 10
                    } : {
                        visible: true,
                        left: bounds.left,
                        bottom: bounds.top + (document.scrollingElement || document.documentElement).scrollTop - 10
                    }
            });
        }
    }

    _onAttachUserClose = () => {
        this.setState({ userList: { visible: false } });
    }

    _onAttachUserChange = (sender, userID) => {
        if (this.props.onAttach) {
            this.props.onAttach(this, { type: AttachmentType.USER, data: userID });
        }
        this._onAttachUserClose();
    }

    _onDetach = (attachment) => {
        if (this.props.onDetach) {
            this.props.onDetach(this, attachment);
        }
    }

    render() {
        const { attachments, attachmentTypes, children } = this.props,
            { userList } = this.state,
            submitButton = { ...defaultProps.submitButton, ...this.props.submitButton },
            cancelButton = { ...defaultProps.cancelButton, ...this.props.cancelButton };

        return (
            <div className={`post-form ${this.props.className} ${this.props.disabled ? 'disabled' : ''}`}>
                {
                    this.props.title ?
                        <FormattedResource tagName="div" className="title" resource={this.props.title} /> :
                        null
                }
                <div className="message">
                    {this.props.formattable ? MarkdownBlock.MarkdownBlockHint : null}
                    <User userID={User.AUTO_CURRENT_USER_ID} firstNameVisible={false} lastNameVisible={false} />
                    <TextInput ref="text-input" autofocus={this.props.autofocus} disabled={this.props.disabled} inline multiline={this.props.multiline} value={this.state.value} placeholder={this.props.placeholder} debounce={0}
                        onChange={this._onChange} onSubmit={this._onSubmit} />
                </div>
                {
                    children && (!Array.isArray(children) || children.some(v => v)) ?
                        <div className="external">
                            {children}
                        </div> :
                        null
                }
                {attachments && attachments.length ? (
                    <div className="attachments">
                        {attachments.map((attachment, index) => {
                            switch (attachment.type) {
                                case AttachmentType.IMAGE:
                                    return (
                                        <div key={attachment.data} className="attachment image">
                                            <img src={attachment.data} />
                                            <div className="overlay">
                                                <button className="inline fa fa-times" onClick={this._onDetach.bind(this, attachment)} disabled={this.props.disabled} />
                                            </div>
                                        </div>
                                    );
                                case AttachmentType.USER:
                                    return (
                                        <div className="attachment user" key={attachment.data}>
                                            <User userID={attachment.data} />
                                            <button className="inline fa fa-times" onClick={this._onDetach.bind(this, attachment)} disabled={this.props.disabled} />
                                        </div>
                                    );
                            }
                        })}
                    </div>
                ) : null}
                {
                    attachmentTypes && attachmentTypes.length || cancelButton.visible || submitButton.visible ?
                        <div className="toolbar row">
                            {
                                attachmentTypes && attachmentTypes.includes(AttachmentType.IMAGE) ?
                                    <span>
                                        <button className="button" disabled={this.props.disabled || attachments && attachments.some(a => a.type === AttachmentType.IMAGE)}
                                            onClick={this._onAttachImageClick}>
                                            <span className="fa fa-image" />
                                            <FormattedResource resource={{ id: 'common.image' }} />
                                        </button>
                                        <input ref="image" type="file" accept="image/gif, image/jpeg, image/png" onChange={this._onAttachImageChange} />
                                    </span> :
                                    null
                            }
                            {
                                attachmentTypes && attachmentTypes.includes(AttachmentType.USER) ?
                                    <span>
                                        <button ref="attach-user"
                                            disabled={this.props.disabled}
                                            className={'button' +
                                                (attachments && attachments.find(attachment => attachment.type === AttachmentType.USER) ? ' has-attachments' : '') +
                                                (userList.visible ? ' active' : '')}
                                            onClick={this._onAttachUserClick}>
                                            <span className="fa fa-bullhorn" />
                                            <FormattedResource resource={{ id: 'common.ask_for_feedback' }} />
                                        </button>
                                        {
                                            userList.visible ?
                                                <Popup className="attach-user-dropdown" fixed={false} {/*TODO use droppanel with custom title*/...userList} onClose={this._onAttachUserClose}>
                                                    <div className="title"><FormattedResource resource={{ id: 'common.ask_for_feedback' }} /></div>
                                                    <UserSelect inline value={null} placeholder={{ id: 'common.search_user' }} onChange={this._onAttachUserChange} />
                                                </Popup> :
                                                null
                                        }
                                    </span> :
                                    null
                            }
                            <div className="main-col" />
                            {
                                cancelButton.visible ?
                                    <span>
                                        <button className="button inline" disabled={this.props.disabled}
                                            onClick={this._onCancel}>
                                            <FormattedResource resource={cancelButton.content} />
                                        </button>
                                        {
                                            cancelButton.tooltip ?
                                                <Tooltip><FormattedResource resource={cancelButton.tooltip} /></Tooltip> :
                                                null
                                        }
                                    </span> :
                                    null
                            }
                            {
                                submitButton.visible ?
                                    <span>
                                        <button className="button primary"
                                            disabled={this.props.disabled || (
                                                typeof this.props.valid === 'boolean' ?
                                                    !this.props.valid :
                                                    !this.state.value && (!attachments || !attachments.length)
                                            )}
                                            onClick={this._onSubmitClick}>
                                            <FormattedResource resource={submitButton.content} />
                                        </button>
                                        {
                                            submitButton.tooltip ?
                                                <Tooltip><FormattedResource resource={submitButton.tooltip} /></Tooltip> :
                                                null
                                        }
                                    </span> :
                                    null
                            }
                        </div> :
                        null
                }
            </div>
        );
    }
}